import type { RevolveFeatureEditDefinition } from './RevolveFeatureEditDefinition';

/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/edit/revolve 层；
 * 2. 负责把旋转编辑定义映射为可执行命令参数；
 * 3. 该类只做纯数据转换，不接触场景、Worker 或 UI。
 *
 * 文件职责：
 * 1. 统一处理方向 1 / 方向 2 角度写回；
 * 2. 统一处理反向时的轴方向翻转；
 * 3. 统一写回 Merge Result，避免多个调用点重复解释默认值。
 */

/**
 * 旋转命令基础参数。
 */
export type RevolveFeatureBaseCommandParams = Omit<
    RevolveParams,
    'direction1Angle' | 'direction2Enabled' | 'direction2Angle' | 'mergeResult'
>;

/**
 * 克隆旋转切除保留体片段键。
 *
 * 设计说明：
 * 1. 该值可能来自 web-client 的 Vue ref 对象；
 * 2. Vue proxy 不能直接参与 structuredClone；
 * 3. 这里用显式遍历复制纯字符串数组，避免 UI 响应式实现泄漏到 cad-core。
 *
 * @param value 原始保留体片段键。
 * @returns 克隆后的保留体片段键。
 */
function cloneKeepShapeKeys(value: Record<string, string[]> | undefined): Record<string, string[]> | undefined {
    if (!value || typeof value !== 'object') {
        return undefined;
    }
    return Object.fromEntries(
        Object.entries(value)
            .filter((entry): entry is [string, string[]] => (
                typeof entry[0] === 'string'
                && Array.isArray(entry[1])
            ))
            .map(([solidId, shapeKeys]) => [
                solidId,
                shapeKeys.filter((shapeKey): shapeKey is string => typeof shapeKey === 'string'),
            ]),
    );
}

/**
 * 旋转特征命令参数构造器。
 */
export class RevolveFeatureCommandParamsBuilder {
    /**
     * 根据基础参数与编辑定义构建命令参数。
     *
     * @param baseParams 基础命令参数。
     * @param definition 编辑定义。
     * @returns 完整命令参数。
     */
    public static buildCommandParams(
        baseParams: RevolveFeatureBaseCommandParams,
        definition: RevolveFeatureEditDefinition,
    ): RevolveParams {
        const direction = definition.directionReversed
            ? this.reverseAxisDirection(baseParams.axis.direction)
            : [...baseParams.axis.direction] as [number, number, number];

        return {
            ...baseParams,
            axis: {
                point: [...baseParams.axis.point] as [number, number, number],
                direction,
            },
            direction1Angle: definition.direction1Angle,
            direction2Enabled: definition.direction2Enabled,
            direction2Angle: definition.direction2Enabled ? definition.direction2Angle : 0,
            mergeResult: definition.mergeResult,
            flipSideToCut: definition.flipSideToCut === true,
            keepShapeKeys: cloneKeepShapeKeys(definition.keepShapeKeys),
            previewToken: definition.previewToken,
        };
    }

    /**
     * 翻转旋转轴方向。
     *
     * @param direction 原始方向。
     * @returns 已翻转方向。
     */
    private static reverseAxisDirection(direction: [number, number, number]): [number, number, number] {
        return direction.map((value) => {
            const nextValue = -value;
            return Object.is(nextValue, -0) ? 0 : nextValue;
        }) as [number, number, number];
    }
}
